import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { history } from "../../router/history";
import { Routes } from "../../constant/routes";
import ACTIONS from "../../store/action";
import { selectIsBulkActionDirty, selectFilterListStatuses } from "../../store/selector";
import BulkActionDiscardPopup from "./BulkActionDiscardPopup";
import { takePendingBulkLeaveTeardown, takeBulkNavConfirmed } from "./pendingBulkLeaveTeardown";

// The pending location the guard blocked is where the user is trying to go. When we chain
// into the saved-list discard modal (both dirty at once), that modal navigates via
// filterListStatuses.nextSelectedView, so translate the blocked target into that view id:
// "/?selectedView=X" → "X"; a bare "/" (dashboard) → Routes.ROOT.
const targetToNextSelectedView = (target) => {
  const selectedView = target?.search ? new URLSearchParams(target.search).get("selectedView") : null;
  return selectedView || Routes.ROOT;
};

/**
 * Global safety-net guard for the "leaving an open Bulk Action" confirmation.
 *
 * Mounted once at the app layout. While any view has an open bulk selection it
 * arms history.block, so EVERY route change is intercepted — the "View List"
 * success toast, browser Back/Forward, and any future cross-route link — not
 * just the navigation paths that were wired up by hand.
 *
 * Scope: only PATHNAME changes are guarded. The sidebar and TailorMed-logo
 * navigation switch views within the dashboard pathname ("/") via Redux and own
 * their own in-place confirmation (SidebarMenu) with no teardown on "Stay", so
 * we deliberately let same-pathname pushes through here to avoid a double
 * prompt. This guard and the SidebarMenu guard therefore cover disjoint sets of
 * navigation.
 *
 * Single-blocker rule: the `history` library allows only ONE active block at a
 * time. Ownership is keyed purely on isBulkActionDirty — this guard arms whenever
 * the bulk action is dirty, and the saved-list <Prompt> in FilterListControls stands
 * down whenever it is (when={!isBulkActionDirty}). So exactly one blocker is ever
 * armed. This guarantees the "Discard bulk action?" popup fires FIRST on every exit
 * path, even when the list ALSO has unsaved filter edits (canModify).
 *
 * On "Discard & Leave":
 *  - bulk-only (no filter edits): clear selections, resume the pending navigation.
 *  - both dirty (canModify): clear selections, then CHAIN to the saved-list "Unsaved
 *    Changes" modal (via filterListStatuses) so the filter edits aren't dropped without
 *    warning — that modal's own Save/Discard drives the leave. No navigation happens here.
 * On "Back": stay — nothing is torn down, the block stays armed.
 */
const BulkActionNavGuard = () => {
  const dispatch = useDispatch();
  const isBulkActionDirty = useSelector(selectIsBulkActionDirty);
  const filterListStatuses = useSelector(selectFilterListStatuses);

  const [pendingLocation, setPendingLocation] = useState(null);
  // Set right before we resume a confirmed navigation so the re-fired block
  // callback lets that exact target through instead of re-prompting.
  const confirmedRef = useRef(false);

  useEffect(() => {
    // Arm whenever the bulk-action panel is dirty (a staged, unapplied edit) — not
    // merely when rows are selected. Ownership is keyed purely on isBulkActionDirty:
    // the saved-list <Prompt> stands down while it's true, so only one history.block is
    // ever live. This guard therefore always shows the bulk popup first, even on a
    // saved list with unsaved filter edits (canModify) — handleDiscard then chains to
    // the filter modal for that case.
    if (!isBulkActionDirty) return undefined;

    const unblock = history.block((location) => {
      if (confirmedRef.current) {
        confirmedRef.current = false;
        return undefined; // allow the navigation we just confirmed
      }
      // The SidebarMenu guard already resolved this bulk-dirty confirmation and is
      // doing a cross-route push (e.g. leaving a saved list for a bucket view). It
      // owns that flow; don't re-prompt for the one push it pre-confirmed.
      if (takeBulkNavConfirmed()) {
        return undefined;
      }
      // Same-pathname (dashboard view switch) is owned by the SidebarMenu guard.
      if (location.pathname === history.location.pathname) {
        return undefined;
      }
      setPendingLocation(location);
      return false; // block; the popup drives what happens next
    });

    return () => {
      unblock();
      // If the guard disarms while a confirmation is still pending — e.g. the bulk
      // selection is cleared out from under us by an external state change, not by the
      // user answering the popup — drop any deferred baton so it can't run against, or
      // wave through, a later unrelated navigation. Harmless no-op on the normal
      // Discard/Stay paths, where the baton was already consumed synchronously.
      takePendingBulkLeaveTeardown();
      takeBulkNavConfirmed();
      setPendingLocation(null);
    };
  }, [isBulkActionDirty]);

  const handleDiscard = () => {
    const target = pendingLocation;
    setPendingLocation(null);
    if (!target) return;
    // Clear selections first so the action bar closes (also resets the dirty flag).
    // This touches only bulk state — no saved-list/filter teardown runs here.
    dispatch(ACTIONS.clearAllBulkSelections());

    // Both dirty at once: the saved list also has unsaved FILTER edits. Discarding the
    // bulk action must NOT silently drop them — chain into the saved-list "Unsaved
    // Changes" modal instead of navigating. Hand the target to that modal via
    // filterListStatuses.nextSelectedView; its own Save/Discard drives the leave. Do NOT
    // push and do NOT arm confirmedRef, so there's no in-flight navigation racing the
    // blocker swap as isBulkActionDirty flips false and the filter <Prompt> re-arms. Drop
    // any deferred teardown baton — the filter modal owns the terminal teardown now.
    //
    // This includes opening the just-saved list via "View List": "Save as New List" saved
    // the edited state into a NEW list, but the ORIGINAL list still has unsaved filter edits,
    // so leaving it must still warn — chain to the filter modal here too.
    if (filterListStatuses?.canModify) {
      dispatch(
        ACTIONS.updateFilterListStatuses(
          filterListStatuses.id,
          filterListStatuses.canModify,
          true,
          targetToNextSelectedView(target)
        )
      );
      takePendingBulkLeaveTeardown();
      return;
    }

    // Bulk-only dirty: this is the terminal leave.
    // Arm the pass-through BEFORE teardown so a throwing teardown can't strand us
    // in a half-torn-down state with the navigation blocked: selections are already
    // cleared, so the push must still fire to keep the app consistent.
    confirmedRef.current = true;
    // Run any view-specific teardown the navigation source deferred to us (e.g. the
    // logo's dashboard reset). The source registers it without running it, so "Stay"
    // leaves the source view untouched; it runs only here, on Discard & Leave.
    const teardown = takePendingBulkLeaveTeardown();
    try {
      if (teardown) teardown();
    } catch (error) {
      // A teardown failure must not swallow the navigation — log and continue.
      console.error("Bulk-action leave teardown failed; continuing navigation", error);
    }
    // Push the location object directly so history owns path encoding (no
    // hand-built string, no chance of forwarding an unvalidated external URL).
    history.push(target);
  };

  const handleStay = () => {
    setPendingLocation(null);
    // Discard the deferred teardown — the user chose to stay, nothing must run.
    takePendingBulkLeaveTeardown();
  };

  if (!pendingLocation) return null;

  return (
    <BulkActionDiscardPopup visible={!!pendingLocation} onClickDiscard={handleDiscard} onClickCancel={handleStay} />
  );
};

export default BulkActionNavGuard;
